import { Box, Typography, Button, Stack, useTheme, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { alpha } from '@mui/material/styles';
import SchoolIcon from '@mui/icons-material/School';
import { GradeLevel, DifficultyLevel } from '../models/User';

interface GradeLevelSelectorProps {
  gradeLevel: GradeLevel | null;
  difficultyLevel: DifficultyLevel | null;
  onGradeLevelChange: (level: GradeLevel) => void;
  onDifficultyLevelChange: (level: DifficultyLevel) => void;
  onSubmit: () => void;
  submitLabel?: string;
  isLoading?: boolean;
}

const GRADE_OPTIONS: { value: GradeLevel; label: string }[] = [
  { value: 'K', label: 'K' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' },
  { value: '6', label: '6' },
  { value: '7', label: '7' },
  { value: '8', label: '8' },
  { value: '9', label: '9' },
  { value: '10', label: '10' },
  { value: '11', label: '11' },
  { value: '12', label: '12' },
  { value: 'college', label: 'College' },
];

const DIFFICULTY_OPTIONS: { value: DifficultyLevel; label: string; description: string }[] = [
  {
    value: 'beginner',
    label: 'Beginner',
    description: 'First time learning this subject',
  },
  {
    value: 'intermediate',
    label: 'Intermediate',
    description: 'Comfortable with the basics',
  },
  {
    value: 'advanced',
    label: 'Advanced',
    description: 'Strong foundation, ready to go deeper',
  },
  {
    value: 'expert',
    label: 'Expert',
    description: 'Near-mastery, want the hardest content',
  },
];

export default function GradeLevelSelector({
  gradeLevel,
  difficultyLevel,
  onGradeLevelChange,
  onDifficultyLevelChange,
  onSubmit,
  submitLabel = 'Continue',
  isLoading = false,
}: GradeLevelSelectorProps) {
  const theme = useTheme();

  const canSubmit = gradeLevel !== null && difficultyLevel !== null;

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <SchoolIcon sx={{ color: 'primary.main', fontSize: 22 }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
          What's your grade level?
        </Typography>
      </Box>

      <ToggleButtonGroup
        value={gradeLevel}
        exclusive
        onChange={(_e, val) => { if (val) onGradeLevelChange(val as GradeLevel); }}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.75,
          '& .MuiToggleButtonGroup-grouped': {
            border: `1px solid ${alpha(theme.palette.primary.main, 0.25)} !important`,
            borderRadius: '8px !important',
            m: 0,
          },
        }}
      >
        {GRADE_OPTIONS.map((opt) => (
          <ToggleButton
            key={opt.value}
            value={opt.value}
            sx={{
              px: opt.value === 'college' ? 1.5 : 1,
              py: 0.75,
              minWidth: opt.value === 'college' ? 70 : 40,
              fontSize: 'body2.fontSize',
              fontWeight: 500,
              textTransform: 'none',
              color: 'text.secondary',
              '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                color: 'primary.main',
                fontWeight: 700,
                borderColor: `${theme.palette.primary.main} !important`,
              },
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.06),
              },
            }}
          >
            {opt.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1.5 }}>
          What's your difficulty level?
        </Typography>
        <Stack spacing={1}>
          {DIFFICULTY_OPTIONS.map((opt) => {
            const selected = difficultyLevel === opt.value;
            return (
              <Box
                key={opt.value}
                onClick={() => onDifficultyLevelChange(opt.value)}
                sx={{
                  px: 2,
                  py: 1.25,
                  borderRadius: 2,
                  border: `1px solid`,
                  borderColor: selected
                    ? 'primary.main'
                    : alpha(theme.palette.primary.main, 0.2),
                  backgroundColor: selected
                    ? alpha(theme.palette.primary.main, 0.08)
                    : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    borderColor: alpha(theme.palette.primary.main, 0.4),
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: selected ? 700 : 500, color: selected ? 'primary.main' : 'text.primary' }}
                >
                  {opt.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {opt.description}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      </Box>

      <Button
        fullWidth
        variant="contained"
        size="large"
        disabled={!canSubmit || isLoading}
        onClick={onSubmit}
        sx={(t) => ({
          py: 1.5,
          borderRadius: 2,
          textTransform: 'none',
          fontSize: 'subtitle1.fontSize',
          backgroundImage: `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.primary.dark})`,
          color: 'primary.contrastText',
          boxShadow: [
            `0 6px 20px ${alpha(t.palette.primary.main, 0.4)}`,
            `inset 0 2px 0 ${alpha(t.palette.common.white, 0.2)}`,
            `inset 0 -2px 0 ${alpha(t.palette.common.black, 0.12)}`,
          ].join(', '),
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          '&:hover': {
            backgroundImage: `linear-gradient(135deg, ${t.palette.primary.light}, ${t.palette.primary.main})`,
            transform: 'translateY(-1px)',
          },
          '&:disabled': {
            backgroundImage: 'none',
          },
        })}
      >
        {submitLabel}
      </Button>
    </Stack>
  );
}
